import React, { useEffect, useRef } from 'react';
import { KeyRound, Layers, Plug, ShieldCheck, X } from 'lucide-react';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Role-aware control',
    body: 'Fine-grained permissions, approvals, and visibility for staff, leads, and partners.',
  },
  {
    icon: Layers,
    title: 'Workflows you own',
    body: 'States, handoffs, and guardrails modelled around your operations—not a generic template.',
  },
  {
    icon: KeyRound,
    title: 'Audit-ready data',
    body: 'History, exports, and structure your finance and compliance conversations can rely on.',
  },
  {
    icon: Plug,
    title: 'Connects to your stack',
    body: 'APIs, imports, and webhooks so admin tools stay part of the same system of record.',
  },
];

/**
 * Full-screen preview for the agency PulseFit Admin / wellness operations thumbnail.
 * Light panel; feature icons use brand purple #996afe.
 */
export function AdminToolExampleModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-hidden={!open}>
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div className="relative z-[101] flex max-h-[100dvh] min-h-full items-center justify-center overflow-y-auto p-3 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-example-title"
          className="portal-modal-panel relative my-auto rounded-2xl border border-neutral-200 bg-white px-4 py-6 shadow-2xl sm:px-8 sm:py-8"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="absolute right-3 top-3 rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600] sm:right-4 sm:top-4"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>

          <header className="pr-10 text-center sm:pr-12 sm:text-left">
            <h2 id="admin-example-title" className="sr-only">
              PulseFit Admin — Wellness Operations Platform
            </h2>
            <img
              src="/assets/images/Admin_logo.webp"
              alt=""
              width={400}
              height={80}
              className="mx-auto h-16 w-auto max-w-full object-contain object-center sm:mx-0 sm:h-20"
              loading="eager"
              decoding="async"
            />
            <p className="mx-auto mt-3 max-w-3xl text-base leading-relaxed text-neutral-600 sm:mx-0 sm:mt-4 sm:text-lg">
              Bespoke operator tools for complex data—roles, workflows, and safeguards in one place,
              <br />
              without forcing your team through off-the-shelf admin chrome.
            </p>
          </header>

          <div className="mt-8 overflow-hidden rounded-xl bg-white">
            <img
              src="/assets/images/Admin_modal_example.webp"
              alt="PulseFit Admin wellness operations platform: interface preview"
              width={1200}
              height={800}
              className="h-auto w-full object-contain"
              loading="eager"
              decoding="async"
            />
          </div>

          <ul className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <li key={title} className="flex gap-3 text-left">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-[#996afe]/25 bg-white text-[#996afe] shadow-sm">
                  <Icon className="h-8 w-8" strokeWidth={1.5} aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-semibold text-neutral-900 sm:text-lg">{title}</p>
                  <p className="mt-1 text-base leading-relaxed text-neutral-600 sm:text-lg">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default AdminToolExampleModal;
